import React, { useRef, useState, useEffect, createContext } from 'react'
import ReactDOM from 'react-dom'
import Icon from '../Icon2/Icon'
import FieldProperties from '../FieldProperties/FieldProperties'
import infoModal from '../InfoModal/InfoModal'
import Tooltip from '../Tooltip/Tooltip'
import { addFieldName, wrapperStyle, fieldForm } from './helper'
import { getTestIdFormatter } from '../../utils/helpers'
import { isEnterPressed } from 'utils/keyboardUtils'
import './ContentTypeField.css'
import Button from '../Button/Button'
import Pill from '../Pills/Pill'
import { Truncate } from '../Truncate/Truncate'


const ContentTypeFieldContext: any = createContext({})

const useContentTypeFieldContext = () => {
  const context = React.useContext(ContentTypeFieldContext)
  return context
}

export const ContentTypeFieldProvider = (props: any) => {
  const state = useState()
  return <ContentTypeFieldContext.Provider value={state}>{props.children}</ContentTypeFieldContext.Provider>
}

const ContentTypeField = (props) => {
  const {
    field,
    options,
    setOptions,
    parentInfo,
    onFieldDelete,
    index,
    disable,
    globalFieldOptions,
    extensionFieldOptions,
    referenceContentType,
    jsonRteContentType,
    rteContentType,
    disableDelete,
    updatedState,
    selectAsNumberText,
    selectAsKeyValue,
    selectAsSingle,
    selectOptionsLimit,
    disableSetting,
    currentFieldPath,
    enableEmbeddedObj,
    canDrag,
    connectDragSource,
    t,
    isNonLocalizable,
    hasNonLocalizablePlan,
    hasNonLocalizablePlanForBlocksAndGroupMultiple,
    hasNonLocFieldsWithinGroupOrGlobalField,
    isCTBuilder,
    isSingle,
    userCustomFieldLimits,
    hasTimePickerConfig,
    isEntryTabsEnabled,
    isUserDefinedZoneEnabled,
    contentTypeSchemaTree,
    isGlobalFieldBuilderPage,
    urlManagementV2Visible = false
  } = props

  const fieldPropRef = useRef(null)
  const fieldOptionRef = useRef(null)

  const [initialField, setField] = useState(field)

  let [isActive, setIsActive] = useState(false)



  const [contextState, setContextState]: any = useContentTypeFieldContext()
  const handleDoubleClick = () => {
    onSettingsClick()
  }
  const handleDeleteClick = () => {
    onFieldDelete && onFieldDelete()
  }
  let handle

  const byDefaultTitleAndUrl = (field) => {
    // no longer need _default pill for URL field
    if ((field.uid === 'title') && field.field_metadata?._default) {
      return true
    }
  }

  const handleSettingsClick = () => {
    if (fieldPropRef.current.childNodes.length === 0) {
      if (contextState && Object.keys(contextState).length) {
        contextState.closeModal()
        setContextState({})
      }
      onSettingsClick()
    } else {
      setIsActive(false)
      destroy(fieldPropRef.current.childNodes[0])
    }
  }


  const handleSettingKeyPress = (e: any): void => {
    if (isEnterPressed(e.key)) {
      e.preventDefault()
      e.stopPropagation()
      handleSettingsClick()
    }
  }

  const handleDeleteKeyPress = (e: React.KeyboardEvent<HTMLDivElement>): void => { if (isEnterPressed(e.key)) { handleDeleteClick() } }

  const allowedEntryTabsFields = ['group', 'global_field', 'blocks']
  const getFieldLabels = (): { id: number, text: string }[] => {
    const items = [];
    const selectFieldTypes = ['dropdown', 'radio', 'checkbox']

    if ((field.multiple && field.data_type !== 'blocks') ||
      (field.data_type === 'reference' && field.field_metadata.ref_multiple))
      items.push({ id: 1, text: 'Multiple' })

    if (byDefaultTitleAndUrl(field))
      items.push({ id: 2, text: 'Default field' })

    if (field.non_localizable)
      items.push({ id: 3, text: 'Non Localizable' })

    if ((selectFieldTypes.includes(field.display_type) || !field?.multiple) && field?.field_metadata?.isTitle && parentInfo?.hasParent && parentInfo?.isMulti)
      items.push({ id: 4, text: 'Group Title' })


    if (field?.field_metadata?.isTab && isEntryTabsEnabled && allowedEntryTabsFields.includes(field?.data_type)) {
      items.push({ id: 5, text: 'Tab' })
    }

    if((selectFieldTypes.includes(field.display_type) || !field?.multiple) && field.field_metadata?.is_global_field_instance_title)
      items.push({ id: 6, text: 'Global Title' })

    if((selectFieldTypes.includes(field.display_type) || !field?.multiple) && parentInfo?.hasParent && parentInfo?.isMulti && field.field_metadata?.is_modular_block_instance_title)
      items.push({ id: 7, text: 'Block Title' })

    return items;
  }

  const items = getFieldLabels();


  const renderComponent = () => {

    return (
      <div
        data-test-id={`cs-ct-${getTestIdFormatter(field?.display_name)}`}
        className={[
          'ContentTypeField__top-level',
          canDrag ? 'draggableHandler' : '',
          field.error ? 'ContentTypeField__error' : ''
        ].join(' ')}
        onDoubleClick={handleDoubleClick}>
        {
          !disable ? <div className={`draggableHandler ${!canDrag ? 'ContentTypeField__disable' : ''}`} data-test-id={`cs-ct-${getTestIdFormatter(field?.display_name)}-option-move`}>
            <Tooltip content="Move" position="top">
              <Icon testId={`cs-ct-${getTestIdFormatter(field?.display_name)}-option-move-icon`} className='ContentTypeField__icon' version='v2' icon="ActionBar" size={`large`} />
            </Tooltip>
          </div> : <></>
        }
        <Icon version='v2' testId={`cs-ct-${getTestIdFormatter(field?.display_name)}-icon`} className='ContentTypeField__main-icon mr-8' icon={field.iconName} size={'medium'} />
        <div className='ContentTypeField__details'>
          <div className="ContentTypeField__field-name flex">
            <h3 className="ContentTypeField__display-name flex-v-center">
              <Truncate testId={`cs-ct-${getTestIdFormatter(field?.display_name)}-truncate`} isResponsive={true} truncateFrom="end" multilineCount={2}>
                {field.display_name}
              </Truncate>
            </h3>
            {items.length > 0 &&
              <div className="ContentTypeField__property">
                <Pill
                  id='ctf-properties'
                  variant='chip'
                  stroke='#6E6B86'
                  shouldHaveBorder={false}
                  items={items}
                  testId={`cs-ct-${getTestIdFormatter(field?.display_name)}-properties-information`}
                />
              </div>
            }
          </div>
          {!disable ?
            <div className='ContentTypeField__option'>
              <Tooltip content="Properties" position="top">
                <Button buttonRef={fieldOptionRef} tabIndex={0} testId={`cs-ct-${getTestIdFormatter(field?.display_name)}-option-properties`} disabled={disable || disableSetting} onlyIcon='true' buttonType='light' icon='v2-Sliders' version='v2' onClick={handleSettingsClick} onKeyUp={disable ? () => { } : handleSettingKeyPress} />
              </Tooltip>
              <Tooltip content="Delete" position="top">
                <Button tabIndex={0} testId={`cs-ct-${getTestIdFormatter(field?.display_name)}-option-delete`} disabled={disable || disableDelete} onlyIcon='true' buttonType='light' icon='v2-Delete' version='v2' onClick={handleDeleteClick} onKeyUp={!disable && !disableDelete ? () => handleDeleteKeyPress : {}} />
              </Tooltip>
            </div> : <></>
          }
        </div >
      </div >
    )
  }


  if (canDrag) {
    handle = connectDragSource(renderComponent(), {
      dropEffect: 'copy'
    })
  }

  const onClose = () => {
    setIsActive(false)
  }

  const handleChange = () => {
    setField({ ...field })
    updatedState()
  }

  const isURlField = (field: any) => {
    if (field && field.fieldUid === 'url' && field.field_metadata && field.field_metadata._default) {
      return true
    }
    return false
  }

  const onSettingsClick = () => {
    let uid: any = field && field.fieldIdentifier
    const isUrl: any = isURlField(field)
    setIsActive(!isActive)
    delete field.openSettingFlag
    let key = `${uid}.${index}`
    let isInsideMultiple = false;
    let ancestorWithMultipleType = null;
    let isAncestorNonLoc = false
    // Extract check result safely
    const checkMultipleResult =
      parentInfo?.hasParent &&
        typeof parentInfo.checkIsChildInsideMultiple === "function"
        ? (hasNonLocalizablePlanForBlocksAndGroupMultiple ? parentInfo.checkIsChildInsideMultiple(true) : parentInfo.checkIsChildInsideMultiple())
        : null;


    if (hasNonLocalizablePlanForBlocksAndGroupMultiple) {
      isInsideMultiple = checkMultipleResult?.isInsideMultiple || false;
      ancestorWithMultipleType = checkMultipleResult?.ancestorWithMultipleType || null;
      isAncestorNonLoc = checkMultipleResult?.isAncestorNonLoc || false
    } else {
      isInsideMultiple = checkMultipleResult?.isInsideMultiple || false;
    }


    let basicForm = fieldForm(
      field,
      options,
      { ...parentInfo, isInsideMultiple, ...(hasNonLocalizablePlanForBlocksAndGroupMultiple && { ancestorWithMultipleType, isAncestorNonLoc }) },
      key,
      globalFieldOptions,
      extensionFieldOptions,
      referenceContentType,
      jsonRteContentType,
      rteContentType,
      handleChange,
      'basic',
      selectAsNumberText,
      selectAsKeyValue,
      selectAsSingle,
      selectOptionsLimit,
      currentFieldPath,
      enableEmbeddedObj,
      hasNonLocalizablePlan,
      isCTBuilder,
      t,
      setOptions,
      hasNonLocalizablePlanForBlocksAndGroupMultiple,
      userCustomFieldLimits,
      undefined,
      undefined,
      undefined,
      isUserDefinedZoneEnabled,
      contentTypeSchemaTree,
      false,
      urlManagementV2Visible
    )
    let advancedForm = fieldForm(
      field,
      options,
      { ...parentInfo, isInsideMultiple, ...(hasNonLocalizablePlanForBlocksAndGroupMultiple && { ancestorWithMultipleType, isAncestorNonLoc }) },
      key,
      globalFieldOptions,
      extensionFieldOptions,
      referenceContentType,
      jsonRteContentType,
      rteContentType,
      handleChange,
      'advanced',
      selectAsNumberText,
      selectAsKeyValue,
      selectAsSingle,
      selectOptionsLimit,
      currentFieldPath,
      enableEmbeddedObj,
      hasNonLocalizablePlan,
      isCTBuilder,
      t,
      setOptions,
      isNonLocalizable,
      userCustomFieldLimits,
      hasNonLocalizablePlanForBlocksAndGroupMultiple,
      hasNonLocFieldsWithinGroupOrGlobalField,
      hasTimePickerConfig,
      isUserDefinedZoneEnabled,
      contentTypeSchemaTree,
      isGlobalFieldBuilderPage,
      urlManagementV2Visible
    )
    let returnObj = infoModal({
      component: (props) => (
        <>
          <FieldProperties
            {...props}
            basicForm={basicForm}
            advancedForm={advancedForm}
            title={field.fieldName || field.field_name}
            icon={field.iconName}
            isSingle={isSingle}
            isCTBuilder={isCTBuilder}
            isUrl={isUrl}
            updatedState={updatedState}
          />
        </>
      ),
      modalProps: {
        onClose,
        targetNodeOrId: fieldPropRef.current,
        closeOnOverlayClick: false,
        closeOnMousedownClick: true,
        closeOnEscape: true,
        wrapperStyle,
        ignoreContainers: ['ReactModalPortal', 'ReactModal__Content', 'Table__body__row', 'Infomodal--field-setting-box']
      },
      alignment: 'field-setting-box'
    })

    const findAncestor: any = (el: any, cls: any) => {
      if (el && el.classList && el.classList.length && el.classList.contains(cls)) {
        return el
      }
      if (el && el.parentElement) {
        if (el.classList && el.classList.length && el.classList.contains(cls)) {
          return el
        } else {
          el = el.parentElement
          return findAncestor(el, cls)
        }
      } else {
        return false
      }
    }

    setContextState(returnObj)

    setTimeout(() => {
      let PageLayout__body: any = document.querySelector('.PageLayout__body')
      let FieldProperties: any = document.querySelector('.FieldProperties')
      let CurrElem = findAncestor(fieldOptionRef.current, 'ContentTypeField')

      let CurrElemOffset = CurrElem.getBoundingClientRect().top - 56;
      let CurrPL = PageLayout__body?.getBoundingClientRect().top + 8;

      let oldTimeStamp = null
      let scrollY = PageLayout__body?.scrollTop
      let scrollDist
      scrollDist =
        PageLayout__body?.scrollTop +
        (CurrElemOffset - CurrPL) +
        20

      function step(newTimestamp) {
        if (oldTimeStamp !== null) {
          // if duration is 0 scrollY will be -Infinity
          scrollY += (scrollDist * (newTimestamp - oldTimeStamp)) / 500

          PageLayout__body.scrollTop = scrollY
          if (scrollY > scrollDist) {
            scrollY = scrollDist
            return (PageLayout__body.scrollTop = scrollDist)
          }
        }
        oldTimeStamp = newTimestamp
        window.requestAnimationFrame(step)
      }
      if (CurrElemOffset + FieldProperties?.offsetHeight > PageLayout__body?.offsetHeight) {
        window.requestAnimationFrame(step)
      }
    })
  }

  function destroy(node: any) {
    if (node) {
      const unmountResult = ReactDOM.unmountComponentAtNode(node)
      if (unmountResult) {
        node.remove()
      }
    }
  }

  useEffect(() => {
    if (fieldPropRef.current) {
      if (fieldPropRef.current.childNodes.length === 0) {
        //if current fieldProp modal is removed by any other newly opening modal, then we need to set the isActive to 'false'
        setIsActive(false)
      }
    }
  }, [contextState])

  useEffect(() => {
    field && addFieldName(field, parentInfo)
    if (fieldOptionRef && fieldOptionRef.current && field && field.openSettingFlag) {
      fieldOptionRef.current.click()
      setIsActive(true)
    } else {
      setIsActive(false)
    }
  }, [])

  return (
    <div className='ContentTypeField__v2'>
      <div
        data-test-id='cs-ct-field'
        className={`ContentTypeField ${isActive ? ' ContentTypeField--active' : ''} ${field.data_type === 'group' && field.schema.length === 0 ? 'ContentTypeField--group' : ''
          }`}>
        <div ref={fieldPropRef}></div>
        {canDrag ? (
          handle
        ) : (
          renderComponent()
        )}
      </div>
    </div>
  )
}

export default React.memo(ContentTypeField)
